import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { sortBy } from "lodash";
import { Observable, map, of } from "rxjs";
import { environment } from "src/environments/environment";
import { BlockMasterData, CropNamesMasterData, DistrictMasterData, InsuranceScheme, NtfpMasterData, PanchayatMasterData, ProductionUnitData, StatesMasterData, VillageMasterData } from "../types";
import { LocalService } from "./storage/storage.service";

@Injectable({
    providedIn: 'root'
})
export class CdnService {

    constructor(private http: HttpClient, private localService: LocalService) { }

    getStates (): Observable<StatesMasterData[]> {
        const localStates = this.localService.get<StatesMasterData[]>('allStateList');
        if (localStates && localStates.length) {
            return of(localStates);
        } else {
            const url = `${environment.cdnUrl}statemaster.json`;
            return this.http.get<StatesMasterData[]>(url, { observe: 'body' }).pipe(
                map((states) => {
                    const stateData = sortBy(states, 'state_name_en');
                    this.localService.set('allStateList', stateData);
                    return stateData;
                })
            );
        }
    }

    getDistricts (stateId: string | number): Observable<DistrictMasterData[]> {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const localDistricts = this.localService.get<DistrictMasterData[]>(`${paddedStateId}-DistrictList`);
        if (localDistricts && localDistricts.length) {
            return of(localDistricts);
        } else {
            const url = `${environment.cdnUrl}state/${paddedStateId}.json`;

            return this.http.get<DistrictMasterData[]>(url, { observe: 'body' }).pipe(
                map((districts) => {
                    const districtData = sortBy(districts, 'district_name_en');
                    this.localService.set(`${paddedStateId}-DistrictList`, districtData);
                    return districtData;
                })
            );
        }
    }

    /**
     * Get all blocks of a district
     * @param stateId State ID
     * @param districtId District ID
     */
    getBlocks (stateId: string | number, districtId: string | number): Observable<BlockMasterData[]> {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const localBlocks = this.localService.get<BlockMasterData[]>(`${districtId}-BlockList`);
        if (localBlocks && localBlocks.length) {
            return of(localBlocks);
        } else {
            const url = `${environment.cdnUrl}state/${paddedStateId}/district/${districtId}.json`;

            return this.http.get<BlockMasterData[]>(url, { observe: 'body' }).pipe(
                map((blocks) => {
                    const blockData = sortBy(blocks, 'block_name_en');
                    this.localService.set(`${districtId}-BlockList`, blockData);
                    return blockData;
                })
            );
        }
    }

    /**
     * Get all panchayats of a block
     * @param stateId State ID
     * @param districtId District ID
     * @param blockId Block ID
     */
    getPanchayats (
        stateId: string | number,
        districtId: string | number,
        blockId: string | number
    ): Observable<PanchayatMasterData[]> {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const localPanchayats = this.localService.get<PanchayatMasterData[]>(`${blockId}-PanchayatList`);
        if (localPanchayats && localPanchayats.length) {
            return of(localPanchayats);
        } else {
            const url = `${environment.cdnUrl}state/${paddedStateId}/district/${districtId}/block/${blockId}.json`;

            return this.http.get<PanchayatMasterData[]>(url, { observe: 'body' }).pipe(
                map((panchayats) => {
                    const panchayatData = sortBy(panchayats, 'panchayat_name_en');
                    this.localService.set(`${blockId}-PanchayatList`, panchayatData);
                    return panchayatData;
                })
            );
        }
    }

    /**
     * Get all villages of a panchayat
     * @param stateId State ID
     * @param districtId District ID
     * @param blockId Block ID
     * @param panchayatdId Panchayat ID
     */
    getVillages (
        stateId: string | number,
        districtId: string | number,
        blockId: string | number,
        panchayatdId: string | number
    ): Observable<VillageMasterData[]> {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const localVillages = this.localService.get<VillageMasterData[]>(`${panchayatdId}-VillageList`);
        if (localVillages && localVillages.length) {
            return of(localVillages);
        } else {
            const url = `${environment.cdnUrl}state/${paddedStateId}/district/${districtId}/block/${blockId}/panchayat/${panchayatdId}.json`;

            return this.http.get<VillageMasterData[]>(url, { observe: 'body' }).pipe(
                map((villages) => {
                    const villageData = sortBy(villages, 'village_name_en');
                    this.localService.set(`${panchayatdId}-VillageList`, villageData);
                    return villageData;
                })
            );
        }
    }

    getCropsName (stateId: number): Observable<CropNamesMasterData[]> {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const url = `${environment.cdnUrl}crop/${paddedStateId}.json`;
        return this.http.get<CropNamesMasterData[]>(url, { observe: 'body' }).pipe(
            map((cropNames) => {
                return cropNames;
            })
        );
    }


    getNtfpActivities(stateId: number): Observable<NtfpMasterData[]> {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const url = `${environment.cdnUrl}ntfp/${paddedStateId}.json`;
        return this.http.get<NtfpMasterData[]>(url, { observe: 'body' }).pipe(
            map((ntfp) => {
                return ntfp;
            })
        );
    }

    /**
     * Get blockName by Block Id
     * @param stateId State ID
     * @param districtId District ID
     * @param blockId Block ID
     */
    getBlockNameByBlockId (stateId: string | number, districtId: string | number, blockId: string | number): Observable<string> {
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const localBlocks = this.localService.get<BlockMasterData[]>(`${districtId}-BlockList`);

        if (localBlocks && localBlocks.length) {
            const filteredBlock = localBlocks.find(block => Number(block.block_id) === Number(blockId));
            return of(filteredBlock ? filteredBlock.block_name_en : '');
        } else {
            const url = `${environment.cdnUrl}state/${paddedStateId}/district/${districtId}.json`;

            return this.http.get<BlockMasterData[]>(url, { observe: 'body' }).pipe(
                map((blocks) => {
                    this.localService.set(`${districtId}-BlockList`, blocks);
                    const filteredBlock = blocks.find(block => Number(block.block_id) === Number(blockId));
                    return filteredBlock ? filteredBlock.block_name_en : '';
                })
            );
        }
    }

    getProductionUnit (): Observable<ProductionUnitData[]> {
        const productionUnits = this.localService.get<ProductionUnitData[]>(
            'allProductionUnitList'
        );
        if (productionUnits && productionUnits.length) {
            return of(productionUnits);
        } else {
            const url = `${environment.cdnUrl}land-measurement-units/01.json`;
            console.log('url', url);
            return this.http.get<ProductionUnitData[]>(url, { observe: 'body' }).pipe(
                map((productionUnits) => {
                    this.localService.set('allProductionUnitList', productionUnits);
                    return productionUnits;
                })
            );
        }
    }

    getInsuranceScheme(insuranceType: string, stateId: string): Observable<InsuranceScheme[]>{
        const paddedStateId = `${stateId}`.padStart(2, '0');
        const url = `${environment.cdnUrl}insurance/${insuranceType}/${paddedStateId}.json`;
        return this.http.get<InsuranceScheme[]>(url, { observe: 'body' }).pipe(
            map((schemes) => {
                return schemes;
            })
        );
    }

    getGeoLocation(): Observable<any> {
        const url = `${environment.cdnUrl.replace("/lokos-masterdata/","")}/keycloak/geo-map.json?qw=1`;
        return this.http.get(url, { observe: 'body', responseType: 'json' });
    }

}
